import { Drilldown } from "../ScoreDetail/useDrilldown";
import { t } from "i18next";
import { SsqVersion } from "src/api/sure-api/ssq/useGetOneSsqVersion.ts";

export type OlapLocalizedLabelContext = {
    ssqVersion?: SsqVersion;
    drilldownState?: Drilldown;
};

type DimensionValueTranslation = (key: string | number, context?: OlapLocalizedLabelContext) => string | null;

const OlapLocalizedLabelTranslations: Record<string, DimensionValueTranslation> = {
    headings: (headingId: string | number, context?: OlapLocalizedLabelContext): string | null => {
        for (const heading of context?.ssqVersion?.parentSsqSsqHeadings ?? []) {
            if (heading.id === Number(headingId)) {
                return heading.ssqHeading.title ?? null;
            }
        }

        return null;
    },
    subheadings: (subheadingId: string | number, context?: OlapLocalizedLabelContext): string | null => {
        const headingId = context?.drilldownState?.last?.id;

        for (const heading of context?.ssqVersion?.parentSsqSsqHeadings ?? []) {
            if (heading.id === Number(headingId)) {
                const subheading = heading.children.find((subheading) => subheading.id === Number(subheadingId));

                if (subheading) {
                    return subheading.ssqHeading.title ?? null;
                }
            }
        }

        return null;
    },
    properties: (propertyId: string | number, context) => {
        const parentId = context?.drilldownState?.last?.id;

        for (const heading of context?.ssqVersion?.parentSsqSsqHeadings ?? []) {
            if (heading.id === Number(parentId)) {
                const property = heading.ssqSsqHeadingSsqProperties.find((property) => {
                    return property.ssqProperty.id === Number(propertyId);
                });

                return property?.ssqProperty.name ?? property?.ssqProperty.title ?? null;
            }

            const subheading = heading.children.find((subheading) => subheading.id === Number(parentId));

            if (subheading) {
                const property = subheading.ssqSsqHeadingSsqProperties.find((property) => {
                    return property.ssqProperty.id === Number(propertyId);
                });

                return property?.ssqProperty.name ?? property?.ssqProperty.title ?? null;
            }
        }

        return null;
    },
    subproperties: (subpropertyId: string | number, context) => {
        const propertyId = context?.drilldownState?.last?.id;

        for (const heading of context?.ssqVersion?.parentSsqSsqHeadings ?? []) {
            for (const property of heading.ssqSsqHeadingSsqProperties) {
                if (property.ssqProperty.id === Number(propertyId)) {
                    const subproperty = property.ssqProperty.children.find((subproperty) => {
                        return subproperty.id === Number(subpropertyId);
                    });

                    return subproperty?.name ?? subproperty?.title ?? null;
                }
            }

            for (const subheading of heading.children) {
                for (const property of subheading.ssqSsqHeadingSsqProperties) {
                    if (property.ssqProperty.id === Number(propertyId)) {
                        const subproperty = property.ssqProperty.children.find((subproperty) => {
                            return subproperty.id === Number(subpropertyId);
                        });

                        return subproperty?.name ?? subproperty?.title ?? null;
                    }
                }
            }
        }

        return null;
    },
    components: (component: string | number): string | null => {
        switch (component) {
            case "product":
                return t("entities.product", "Product");
            case "ingredients":
                return t("questionnaire.navigation.ingredients", "Ingredients");
            case "packagings":
                return t("questionnaire.navigation.packagings", "Packagings");
            case "transformations":
                return t("score.components.transformations", "Processing");
            case "transport":
                return t("score.components.transport", "Transport");
            case "distribution":
                return t("score.components.distribution", "Distribution");
            case "consumption":
                return t("score.components.consumption", "Consumption");
            case "suppliers":
                return t("score.components.suppliers", "Suppliers");
            default:
                return null;
        }
    },
};

type LocalizableDimension = keyof typeof OlapLocalizedLabelTranslations;

export class OlapLocalizedLabelService {
    public static localize(
        dimension: LocalizableDimension,
        key: string | number,
        context?: OlapLocalizedLabelContext,
    ): string | null {
        return OlapLocalizedLabelTranslations[dimension]?.(key, context) ?? null;
    }
}
